import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { colord, extend } from "colord";
import mixPlugin from "colord/plugins/mix";
import { firstValueFrom } from 'rxjs';
import { CommunityDTO } from 'src/app/DTO/CommunityDTO';
import { communityChromeColor } from 'src/app/helpers/community-color.helper';
import { Functions } from 'src/app/helpers/Functions';

extend([mixPlugin]);

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  public customLogo: boolean;

  private _appName: string;

  constructor(
    private actRoute: ActivatedRoute
  ) { }

  public get appName() {
    return this._appName;
  }

  public setTheme(c: CommunityDTO){
    this._appName = c?.appTitle;
    this.customLogo = c?.appTitle != null;
    this.setThemeColorVariables(c);
  }

  public async getIsIframe() {
    // Prefer the browser URL: root ActivatedRoute.queryParams can still be empty
    // when AppComponent.ngAfterViewInit runs CookieConsent (race with first emission).
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).has('iframe');
    }

    const params = await firstValueFrom(this.actRoute.queryParams);
    return params['iframe'] !== undefined;
  }

  public static getThemeCssVariablesByCommunity(c: CommunityDTO){
    let ret = '';
    const isCustomCommunity = c.customAppId != null;

    if(c){

      if(c.color1 && c.color2){
        const chrome = communityChromeColor(c) || c.color1;
        ret += `
        --corp-color-gradient-from: ${c.color1};
        --corp-color-gradient-to: ${c.color2};
        --color-primary: ${c.colorPrimary || chrome};
        --corp-color-1: ${chrome};
        --corp-color-2: ${colord(c.color1).mix(c.color2, 0.25).toHex()};
        --corp-color-3: ${colord(c.color1).mix(c.color2, 0.5).toHex()};
        --corp-color-4: ${colord(c.color1).mix(c.color2, 0.75).toHex()};
        --corp-color-5: ${c.color2};

        --corp-background: linear-gradient(90deg, var(--corp-color-gradient));

        `;
      }
    }

    return ret;
  }

  public applyThemeVariables(c: CommunityDTO): void {
    if (!c) return;

    const root = document.documentElement;

    const chrome = communityChromeColor(c);
    if (chrome) {
      root.style.setProperty('--corp-color-1', chrome);
    }
  }

  private setThemeColorVariables(c: CommunityDTO) {
    const body = document.querySelector('body');
    if (!body) return;

    const chrome = communityChromeColor(c) || c?.color1;
    const primaryColor = c?.colorPrimary || chrome || '#212529';

    body.classList.remove(
      'corp-background-is-light',
      'corp-background-is-dark',
      'corp-primary-is-light',
      'corp-primary-is-dark'
    );

    const classes = ThemeService.getThemeCssClassesByCommunity(c, primaryColor);
    body.style.setProperty('--color-primary', primaryColor);
    body.classList.add(...classes.split(' ').filter(x => x));
  }

  private static getThemeCssClassesByCommunity(c: CommunityDTO, fallbackPrimaryColor: string) {
    let ret = '';

    if (c) {
      if (c.color1 && c.color2) {
        // Prefer plain main background for contrast; otherwise gradient midpoint.
        const bgForContrast = communityChromeColor(c) || colord(c.color1).mix(c.color2, 0.5).toHex();

        if (Functions.ColorIsLight(bgForContrast)) {
          ret += `corp-background-is-light `;
        } else {
          ret += `corp-background-is-dark `;
        }
      } else {
        // If there's no gradient, the header uses plain main background (colorBackground) or color1.
        const backgroundColor = communityChromeColor(c) || '#212529';
        if (Functions.ColorIsLight(backgroundColor)) {
          ret += `corp-background-is-light `;
        } else {
          ret += `corp-background-is-dark `;
        }
      }

      const primaryColor = c.colorPrimary || communityChromeColor(c) || fallbackPrimaryColor;
      if (primaryColor) {
        if (Functions.ColorIsLight(primaryColor)) {
          ret += `corp-primary-is-light `;
        } else {
          ret += `corp-primary-is-dark `;
        }
      }
    }

    return ret;
  }

  private static shade(c: string){
    return colord(c).darken(0.07).desaturate(0.27).toHex();
  }

  private static tint(c: string){
    return colord(c).lighten(0.05).toHex();
  }
   public getColorWithOpacity(color: string, opacity: number): string {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    if (color.startsWith('rgb')) {
      return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }
    return color;
  }

}
